import React, { useMemo, useState } from 'react'
import { ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import PreviewListToolbar from './PreviewListToolbar'

export default function GamingVenueCheckPanel({ venueCheck }) {
  const [search, setSearch] = useState('')
  const [pageSize, setPageSize] = useState(15)
  const [offset, setOffset] = useState(0)

  const discrepancy = !!venueCheck?.has_discrepancy
  const needle = search.trim().toLowerCase()
  const fileNames = venueCheck?.file_venue_names || []
  const configured = venueCheck?.configured_venues || []

  const filteredFile = useMemo(() => {
    if (!needle) return fileNames
    return fileNames.filter((name) => String(name).toLowerCase().includes(needle))
  }, [fileNames, needle])

  const filteredConfigured = useMemo(() => {
    if (!needle) return configured
    return configured.filter((v) => {
      const hay = `${v.name || ''} ${v.gaming_extract_venue_name || ''} ${v.venue_id || ''}`.toLowerCase()
      return hay.includes(needle)
    })
  }, [configured, needle])

  if (!venueCheck) return null

  const totalMatched = Math.max(filteredFile.length, filteredConfigured.length)
  const filePage = filteredFile.slice(offset, offset + pageSize)
  const configPage = filteredConfigured.slice(offset, offset + pageSize)
  const hasMore = offset + pageSize < totalMatched
  const showToolbar = fileNames.length > 8 || configured.length > 8 || !!needle

  return (
    <div className={`rounded-lg border p-4 text-sm ${
      discrepancy ? 'border-amber-300 bg-amber-50 text-amber-950' : 'border-green-200 bg-green-50 text-green-900'
    }`}>
      <div className="flex gap-2 items-start">
        {discrepancy
          ? <ExclamationTriangleIcon className="w-5 h-5 shrink-0 text-amber-600" />
          : <CheckCircleIcon className="w-5 h-5 shrink-0 text-green-600" />}
        <div className="flex-1 min-w-0">
          <p className="font-medium">
            {discrepancy ? 'Venue name mismatch' : 'Venue names match configuration'}
          </p>
          {venueCheck.warning && (
            <p className="mt-1 text-xs">{venueCheck.warning}</p>
          )}
        </div>
      </div>

      {showToolbar && (
        <div className="mt-3">
          <PreviewListToolbar
            search={search}
            onSearchChange={(v) => { setSearch(v); setOffset(0) }}
            searchPlaceholder="Filter venue names…"
            pageSize={pageSize}
            onPageSizeChange={(n) => { setPageSize(n); setOffset(0) }}
            offset={offset}
            pageCount={Math.max(filePage.length, configPage.length)}
            totalMatched={totalMatched}
            hasMore={hasMore}
            onPrev={() => setOffset((o) => Math.max(0, o - pageSize))}
            onNext={() => setOffset((o) => o + pageSize)}
          />
        </div>
      )}

      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
        <div>
          <p className="font-medium text-gray-700 mb-1">
            In file (sample){needle ? ` · ${filteredFile.length}` : ''}
          </p>
          {filePage.length ? (
            <ul className="list-disc list-inside space-y-0.5">
              {filePage.map((name) => (
                <li key={name} className="font-mono">
                  {name}
                  {venueCheck.unmatched_file_venues?.includes(name) && (
                    <span className="text-amber-800 ml-1">— no match</span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500">{needle ? 'No matches' : 'No site names detected in sample'}</p>
          )}
        </div>
        <div>
          <p className="font-medium text-gray-700 mb-1">
            Configured venues{needle ? ` · ${filteredConfigured.length}` : ''}
          </p>
          {configPage.length ? (
            <ul className="space-y-1">
              {configPage.map((v) => (
                <li key={v.venue_id}>
                  <span className="font-medium">{v.name}</span>
                  {v.gaming_extract_venue_name && (
                    <span className="text-gray-600"> · extract: {v.gaming_extract_venue_name}</span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500">{needle ? 'No matches' : 'No venues in tenant — add venues in Admin'}</p>
          )}
        </div>
      </div>

      {venueCheck.configured_venues_not_in_file?.length > 0 && (
        <p className="mt-2 text-xs text-gray-600">
          Not in file sample: {venueCheck.configured_venues_not_in_file.map((v) => v.name).join(', ')}
        </p>
      )}

      {discrepancy && (
        <p className="mt-2 text-xs text-amber-900">
          Update <strong>Gaming extract name</strong> on each venue (Admin → Venues) to match the site label in the extract file, or filter the file to the intended site before ingest.
        </p>
      )}
    </div>
  )
}
